import { Music, Play } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ARRIVAL_SOUND_IDS, arrivalSoundLabel } from './arrival-chime'
import type { ArrivalSoundControls } from './hooks/use-arrival-chime'
import { T, tt } from './i18n'

/**
 * Which sound a new order makes on this device - FRONTEND.md §9.676, owner O57 and O68.
 *
 * <p>The owner: *"zmenit zvuk na príchod novej objednávky"* and *"rozvoz nástenka zmeniť zvuk príchodu novej obj"*;
 * answered (#122) with *a sound picker per device - three or four sounds with a preview, on the screens that chime*.
 * So it sits beside the sound switch on the kitchen, the dispatch board and Otvorené stoly, and all three read and
 * write the same answer.
 *
 * <p><b>Each sound has its own ▶</b>, which plays it whether or not sound is switched on: a sound is chosen before it
 * is switched on. Choosing one keeps it and plays it too.
 *
 * <p><b>A small list under its button, with no full-screen backdrop.</b> It closes on Escape, on a press anywhere
 * else and from its own button. A backdrop would be a new silent overlay for `modal-semantics.test.ts` to count, and
 * this is a menu rather than a dialog: nothing behind it needs to be held off while it is open.
 *
 * <p>In `@agos/shared` rather than in a module, because the kitchen and Otvorené stoly are `manager-orders` and the
 * board is `manager-delivery`, which may not import each other.
 */
export function ArrivalSoundPicker({
  chime,
  className,
}: {
  chime: ArrivalSoundControls
  /** The trigger's classes, so it matches the switch beside it - the kitchen's header is dark. */
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const closeIfOutside = (event: PointerEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', closeIfOutside)
    return () => document.removeEventListener('pointerdown', closeIfOutside)
  }, [open])

  const title = tt('Zvuk novej objednávky')

  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        data-sound-picker-open
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={title}
        title={title}
        onClick={() => setOpen((value) => !value)}
        className={
          className ??
          'p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
        }
      >
        <Music className="w-4 h-4" />
      </button>
      {open && (
        <div
          data-sound-picker
          className="absolute right-0 top-full mt-1 z-50 w-60 rounded-xl border border-border bg-card text-foreground shadow-xl py-1"
        >
          <p className="px-3 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          {ARRIVAL_SOUND_IDS.map((id) => {
            const chosen = id === chime.choice
            const label = arrivalSoundLabel(id)
            return (
              <div key={id} className="flex items-center gap-1 px-1">
                <button
                  type="button"
                  data-sound-choice={id}
                  aria-pressed={chosen}
                  onClick={() => chime.choose(id)}
                  className="flex-1 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left hover:bg-muted transition-colors"
                >
                  <span
                    aria-hidden="true"
                    className={
                      chosen
                        ? 'w-3.5 h-3.5 shrink-0 rounded-full border-4 border-primary'
                        : 'w-3.5 h-3.5 shrink-0 rounded-full border border-border'
                    }
                  />
                  <span className={chosen ? 'font-semibold' : undefined}>{label}</span>
                </button>
                <button
                  type="button"
                  data-sound-preview={id}
                  aria-label={`${tt('Prehrať')}: ${label}`}
                  title={`${tt('Prehrať')}: ${label}`}
                  onClick={() => chime.preview(id)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Play className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          })}
          <p className="px-3 pt-1 pb-1.5 text-[11px] text-muted-foreground">
            <T>Platí pre toto zariadenie.</T>
          </p>
        </div>
      )}
    </div>
  )
}
